import { IInvoiceAdjustmentPreviewRow, IInvoiceAdjustmentSummary } from "@/api_framework/api_modals/payment";
import StatusBadge from "@/app/components/ui/Badges/StatusBadge";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import TableComponent, { TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import { formatAmount } from "@/app/components/routes/auth_routes/SlotsScreen/ComfirmationComponent/utils/confirmationHelpers";
import React, { useMemo } from "react";

type Props = {
  fileName: string;
  summary: IInvoiceAdjustmentSummary | null;
  title: string;
  submitLabel: string;
  isSubmitting: boolean;
  onCancel: () => void;
  onSubmit: () => void;
};

type IPreviewRow = IInvoiceAdjustmentPreviewRow & { index: number; remark: string; isError: boolean };

const UploadPreviewComponent: React.FC<Props> = ({
  fileName,
  summary,
  title,
  submitLabel,
  isSubmitting,
  onCancel,
  onSubmit,
}) => {
  const rows = useMemo<IPreviewRow[]>(() => {
    const seen = new Set<string>();
    return (summary?.invoices ?? []).map((row, index) => {
      const isRepeat = seen.has(row.invoice_id);
      seen.add(row.invoice_id);
      if (row.due_amount === null) return { ...row, index, remark: "Skipped: Invoice not found", isError: true };
      if (row.status === "cancelled") return { ...row, index, remark: "Skipped: Invoice is cancelled", isError: true };
      if (row.due_amount <= 0) return { ...row, index, remark: "Skipped: No due amount", isError: true };
      return { ...row, index, remark: isRepeat ? "Repeated, closed once" : "OK", isError: false };
    });
  }, [summary]);

  const columns: TableColumn<IPreviewRow>[] = [
    { id: "index", header: "#", cell: ({ row }) => row.index + 1, width: 48 },
    {
      id: "invoice_id",
      header: "Invoice ID",
      cell: ({ row }) => <span className="font-mono text-[11px] text-gray-900 break-all">{row.invoice_id}</span>,
    },
    {
      id: "invoice_number",
      header: "Invoice Number",
      cell: ({ row }) => <span className="whitespace-nowrap">{row.invoice_number || "-"}</span>,
    },
    {
      id: "due_amount",
      header: "Due Amount",
      width: 120,
      cell: ({ row }) => <span className="whitespace-nowrap">{row.due_amount === null ? "-" : formatAmount(row.due_amount)}</span>,
    },
    {
      id: "remark",
      header: "Remark",
      cell: ({ row }) => (
        <StatusBadge
          label={row.remark}
          colorClassName={row.isError ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}
          statusFillClassname={row.isError ? "fill-red-500" : "fill-green-500"}
        />
      ),
    },
  ];

  const hasInvalid = (summary?.invalid_count ?? 0) > 0;
  const badge = !summary?.is_valid
    ? { label: "Nothing to run", colorClassName: "bg-red-50 text-red-700", fillClassName: "fill-red-500" }
    : hasInvalid
      ? { label: `${summary.invalid_count} will be skipped`, colorClassName: "bg-orange-50 text-orange-700", fillClassName: "fill-orange-500" }
      : { label: "Ready to run", colorClassName: "bg-green-50 text-green-700", fillClassName: "fill-green-500" };

  const infoItems = summary
    ? [
        { label: "Total Records", value: summary.total_records },
        { label: "Amount to Settle", value: formatAmount(summary.total_amount_to_settle) },
        { label: "Invoices to Settle", value: summary.invoices_to_settle_count },
        { label: "Not Found", value: summary.invoice_not_found_count, isError: summary.invoice_not_found_count > 0 },
        { label: "Cancelled", value: summary.cancelled_count, isError: summary.cancelled_count > 0 },
        { label: "Zero Due", value: summary.zero_due_count, isError: summary.zero_due_count > 0 },
        { label: "Duplicates", value: summary.duplicate_count },
      ]
    : [];

  return (
    <ModalPrompt
      modalId="INVOICE_ADJUSTMENT_PREVIEW_MODAL"
      title={
        <span className="flex flex-wrap items-center gap-2">
          {title}
          {summary ? (
            <StatusBadge
              label={badge.label}
              colorClassName={badge.colorClassName}
              statusFillClassname={badge.fillClassName}
            />
          ) : null}
        </span>
      }
      persistent={isSubmitting}
      isshowCrossIcon={!isSubmitting}
      dialogClassName="w-full sm:my-8 sm:max-w-5xl p-4 sm:p-6"
      content={
        summary ? (
          <div className="mt-2 space-y-4">
            <p className="text-xs text-gray-500">
              File · <span className="font-medium text-gray-900 break-all">{fileName}</span>
            </p>

            <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3 rounded-xl border border-gray-200 bg-[#F9FAFB] p-4">
              {infoItems.map((item) => (
                <div key={item.label} className="min-w-0">
                  <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{item.label}</dt>
                  <dd className={`mt-0.5 text-sm font-medium break-words ${item.isError ? "text-[#FF0303]" : "text-gray-900"}`}>
                    {item.value}
                  </dd>
                </div>
              ))}
            </dl>

            {!summary.is_valid ? (
              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                No invoice in this file can be processed. An invoice must exist, not be cancelled and have a due
                amount above 0. Fix the file and upload it again.
              </p>
            ) : hasInvalid ? (
              <p className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-700">
                {summary.invalid_count} invalid invoice(s) marked in red will be skipped. Only the{" "}
                {summary.invoices_to_settle_count} valid invoice(s) will be processed. Download the result from the
                table afterwards for every row and its reason.
              </p>
            ) : null}

            <div>
              <p className="mb-2 text-xs font-semibold text-gray-700">Invoices</p>
              <TableComponent<IPreviewRow>
                columns={columns}
                data={rows}
                getRowId={(row) => `${row.invoice_id}-${row.index}`}
                rowClassName={(row) => (row.isError ? "bg-[#FFF1F1]" : undefined)}
                stickyHeader
                wrapCells
                scrollContainerClassName="max-h-[320px]"
              />
            </div>
          </div>
        ) : null
      }
      button={{
        primaryButtonText: isSubmitting ? "Processing..." : submitLabel,
        secondaryButtonText: "Cancel",
        primaryButtonClassname: "bg-black hover:bg-gray-800",
        primaryButtonDisabled: !summary?.is_valid || isSubmitting,
        secondaryButtonDisabled: isSubmitting,
        primaryCallback: onSubmit,
        secondaryCallback: onCancel,
        onCrossClick: onCancel,
        shouldHideOnClick: false,
      }}
    />
  );
};

export default UploadPreviewComponent;
